<template>
  <div class="formulario-modelo">
    <h1>Asignar Evaluadores por Indicador</h1>
    <div class="divider"></div>

    <div class="info-version" v-if="idVersion">
      <span class="badge">Versión #{{ idVersion }}</span>
    </div>

    <!-- Alerta si la versión no está completada -->
    <div v-if="!versionAprobada" class="alerta-version">
      La versión no está en estado <strong>completado</strong>. No se pueden asignar evaluadores hasta que la versión sea completada.
    </div>

    <!-- Lista de indicadores con sus evaluadores -->
    <div v-if="loadingIndicadores" class="info">Cargando indicadores...</div>
    <div v-else-if="!indicadores.length" class="mensaje-vacio">No hay indicadores disponibles para esta versión.</div>

    <div v-else class="indicadores-list">
      <div v-for="ind in indicadores" :key="ind.id_indicador" class="indicador-card">
        <div class="indicador-header" @click="toggleIndicador(ind.id_indicador)">
          <div class="indicador-info">
            <strong>{{ ind.nombre }}</strong>
            <small>ID: {{ ind.id_indicador }}</small>
          </div>
          <span class="toggle-icon">{{ expandido.has(ind.id_indicador) ? '▾' : '▸' }}</span>
        </div>

        <div v-if="expandido.has(ind.id_indicador)" class="indicador-body">
          <!-- Evaluadores asignados -->
          <div v-if="asignacionesPorIndicador[ind.id_indicador]?.length" class="evaluadores-list">
            <div
              v-for="asig in asignacionesPorIndicador[ind.id_indicador]"
              :key="asig.id"
              class="evaluador-item"
            >
              <span>Usuario #{{ asig.id_usuario }} <small>(asignado por #{{ asig.id_asignado_por }})</small></span>
              <div class="evaluador-acciones">
                <button class="btn btn-sm guardar" @click.stop="irAEvaluar(ind.id_indicador, asig.id_usuario)">Evaluar</button>
                <button class="btn btn-sm salir" @click="confirmarEliminar(asig.id!)">✕</button>
              </div>
            </div>
          </div>
          <p v-else class="mensaje-vacio-sm">Sin evaluadores asignados.</p>

          <div class="asignar-form">
            <input
              v-model.number="nuevoUsuario[ind.id_indicador]"
              type="number"
              min="1"
              placeholder="ID del usuario evaluador"
              class="form-control form-sm"
            />
            <button
              class="btn btn-sm guardar"
              :disabled="!nuevoUsuario[ind.id_indicador]"
              @click="asignarEvaluador(ind.id_indicador)"
            >
              Asignar
            </button>
          </div>

          <!-- Errores -->
          <div v-if="erroresPorIndicador[ind.id_indicador]" class="error-msg">
            {{ erroresPorIndicador[ind.id_indicador] }}
          </div>
        </div>
      </div>
    </div>

    <div class="botones">
      <button class="btn salir" @click="$router.back()">Volver</button>
    </div>

    <MensajeConfirmacion
      :visible="showConfirmacion"
      title="Eliminar Asignación"
      message="¿Desea eliminar esta asignación de evaluador?"
      confirmText="Eliminar"
      cancelText="Cancelar"
      @confirm="ejecutarEliminar"
      @cancel="showConfirmacion = false"
    />

    <MensajeToast ref="toastRef" />
  </div>
</template>

<script setup lang="ts">
/**
 * Reparto del trabajo de evaluación sobre los indicadores.
 *
 * Tres tareas: evidencias, instrumentos y calificar. Una asignación sin
 * indicador designa al responsable del proceso.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `/version/:id_version/evaluadores` (AsignarEvaluadores) — permiso `procesos.asignar`
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useGestionIndicadorEvaluadores } from '@/store/GestionIndicadorEvaluadores'
import { useGestionIndicadores } from '@/store/GestionIndicadores'
import { useGestionVersiones } from '@/store/GestionVersiones'
import type Indicador from '@/interfaces/Indicador'
import MensajeConfirmacion from '@/components/MensajeConfirmacion.vue'
import MensajeToast from '@/components/MensajeToast.vue'

const route = useRoute()
const router = useRouter()
const idVersion = Number(route.params.id_version)
const evalStore = useGestionIndicadorEvaluadores()
const indicadoresStore = useGestionIndicadores()
const versionesStore = useGestionVersiones()
const toastRef = ref<InstanceType<typeof MensajeToast> | null>(null)

const indicadores = ref<Indicador[]>([])
const loadingIndicadores = ref(false)
const versionAprobada = ref(true)
const expandido = ref<Set<number>>(new Set())
const nuevoUsuario = reactive<Record<number, number | null>>({})
const erroresPorIndicador = reactive<Record<number, string>>({})
const showConfirmacion = ref(false)
const idEliminar = ref<number | null>(null)

// Simular id del usuario actual (en producción vendría del store de auth)
const idUsuarioActual = 1

function showToast(msg: string, tipo: 'exito' | 'error' = 'exito') {
  toastRef.value?.showToast(msg, tipo)
}

const asignacionesPorIndicador = computed(() => {
  const map: Record<number, typeof evalStore.asignaciones> = {}
  for (const a of evalStore.asignaciones) {
    if (a.id_indicador == null) continue
    if (!map[a.id_indicador]) map[a.id_indicador] = []
    map[a.id_indicador].push(a)
  }
  return map
})

function irAEvaluar(idIndicador: number, idUsuario: number) {
  router.push({
    name: 'EvaluacionIndividual',
    params: { id_version: idVersion, id_indicador: idIndicador },
    query: { id_usuario: String(idUsuario) }
  })
}

function toggleIndicador(id: number) {
  if (expandido.value.has(id)) {
    expandido.value.delete(id)
  } else {
    expandido.value.add(id)
  }
}

async function asignarEvaluador(idIndicador: number) {
  const idUsuario = nuevoUsuario[idIndicador]
  if (!idUsuario || idUsuario <= 0) return
  delete erroresPorIndicador[idIndicador]

  const result = await evalStore.asignar({
    id_version: idVersion,
    id_indicador: idIndicador,
    id_usuario: idUsuario,
    id_asignado_por: idUsuarioActual
  })

  if (result.ok) {
    showToast('Evaluador asignado')
    nuevoUsuario[idIndicador] = null
  } else {
    const msg = result.errors
      ? Object.values(result.errors).flat().join('. ')
      : 'Error al asignar'
    erroresPorIndicador[idIndicador] = msg
    showToast(msg, 'error')
  }
}

function confirmarEliminar(id: number) {
  idEliminar.value = id
  showConfirmacion.value = true
}

async function ejecutarEliminar() {
  showConfirmacion.value = false
  if (!idEliminar.value) return
  const result = await evalStore.eliminar(idEliminar.value)
  if (result.ok) {
    showToast('Asignación eliminada')
  } else {
    showToast('Error al eliminar asignación', 'error')
  }
  idEliminar.value = null
}

onMounted(async () => {
  if (!Number.isFinite(idVersion) || idVersion <= 0) return
  loadingIndicadores.value = true
  try {
    const version = await versionesStore.getVersionById(idVersion)
    if (!version || version.estado !== 'completado') {
      versionAprobada.value = false
      loadingIndicadores.value = false
      return
    }
    await Promise.all([
      indicadoresStore.cargarTodos(),
      evalStore.cargar({ id_version: idVersion })
    ])
    indicadores.value = indicadoresStore.indicadores
  } finally {
    loadingIndicadores.value = false
  }
})
</script>

<style scoped>
.formulario-modelo {
  background-color: white;
  padding: 2rem;
  border-radius: var(--radius-lg);
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  box-shadow: var(--shadow-md);
  box-sizing: border-box;
  margin: 20px;
  font-family: var(--font-family);
}

h1 { font-size: 1.4rem; font-weight: 700; margin: 0; }
.divider { border: none; border-bottom: 1px solid #e0e0e0; margin: 12px 0 16px; }

.info-version { margin-bottom: 1rem; }
.badge {
  display: inline-block; padding: 4px 10px; border-radius: 4px;
  background-color: #e8f4fd; color: #0e69a0; font-weight: 600; font-size: 0.85rem;
}

.info { padding: 1rem 0; color: #555; }
.mensaje-vacio { color: #888; font-style: italic; padding: 1rem 0; }

.alerta-version {
  background-color: #fff3cd; border: 1px solid #ffc107; border-radius: 8px;
  padding: 1rem; margin-bottom: 1.5rem; color: #856404; font-size: 0.9rem;
}
.mensaje-vacio-sm { color: #999; font-size: 0.85rem; margin: 0.5rem 0; }

.indicadores-list { display: flex; flex-direction: column; gap: 0.5rem; }

.indicador-card {
  border: 1px solid #e0e0e0; border-radius: 8px; overflow: hidden;
}

.indicador-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 0.75rem 1rem; background: #fafafa; cursor: pointer;
  transition: background 0.15s ease;
}
.indicador-header:hover { background: #f0f0f0; }

.indicador-info { display: flex; flex-direction: column; gap: 0.2rem; }
.indicador-info strong { font-size: 0.95rem; }
.indicador-info small { color: #888; font-size: 0.75rem; }

.toggle-icon { font-size: 1.2rem; color: #888; }

.indicador-body { padding: 1rem; border-top: 1px solid #e0e0e0; }

.evaluadores-list { display: flex; flex-direction: column; gap: 0.4rem; margin-bottom: 0.75rem; }
.evaluador-item {
  display: flex; justify-content: space-between; align-items: center;
  padding: 0.4rem 0.75rem; background: #f8f9fa; border-radius: 6px; font-size: 0.9rem;
}
.evaluador-item small { color: #888; }
.evaluador-acciones { display: flex; gap: 0.3rem; }

.asignar-form {
  display: flex; gap: 0.5rem; align-items: center; margin-top: 0.5rem;
}
.form-sm { max-width: 200px; }

.error-msg {
  margin-top: 0.5rem; color: #c00; font-size: 0.85rem;
}

.form-control {
  padding: 0.5rem 0.75rem; border: 1px solid #ddd; border-radius: 8px;
  font-size: 0.9rem; font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.form-control:focus {
  outline: none; border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}

.botones { display: flex; gap: 0.75rem; justify-content: flex-end; margin-top: 1.5rem; }

.btn {
  padding: 0.6rem 1.25rem; font-weight: 600; border: none; border-radius: 8px;
  cursor: pointer; transition: all 0.15s ease; font-family: var(--font-family); font-size: 0.9rem;
}
.btn-sm { padding: 0.35rem 0.75rem; font-size: 0.8rem; }
.btn.salir { background-color: white; border: 1px solid #ccc; color: #333; }
.btn.salir:hover { background-color: #d41a1a; color: white; }
.btn.guardar { background-color: #eea222; color: black; }
.btn.guardar:hover { background-color: #0e69a0; color: white; }
.btn.guardar:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
